(() => {
  if (window.K365SizeGuide) return;

  const initializedRoots = new WeakSet();
  const dialogOpeners = new WeakMap();

  const validAnchors = new Set([
    'how-to-measure',
    'label-conversion',
    'baby-clothing',
    'toddler-clothing',
    'bottoms',
    'hats',
    'socks',
    'essentials'
  ]);

  function getAnchorFromHash(hash = window.location.hash) {
    const value = hash.replace(/^#/, '');
    if (!value) return '';

    try {
      const decoded = decodeURIComponent(value).toLowerCase();
      return validAnchors.has(decoded) ? decoded : '';
    } catch (_error) {
      return '';
    }
  }

  function setAccordionItem(item, open) {
    if (!item) return;

    const trigger = item.querySelector('[data-k365-accordion-trigger]');
    const panel = item.querySelector('[data-k365-accordion-panel]');
    if (!trigger || !panel) return;

    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;
  }

  function closeSiblingItems(item) {
    const accordion = item?.closest('[data-k365-accordion]');
    if (!accordion) return;

    accordion.querySelectorAll('[data-k365-accordion-item]').forEach((candidate) => {
      if (candidate !== item) setAccordionItem(candidate, false);
    });
  }

  function openAnchor(root, anchor, options = {}) {
    if (!root || !validAnchors.has(anchor)) return false;

    const target = Array.from(root.querySelectorAll('[data-k365-anchor]')).find(
      (element) => element.dataset.k365Anchor === anchor
    );
    if (!target) return false;

    const accordionItem = target.matches('[data-k365-accordion-item]')
      ? target
      : target.closest('[data-k365-accordion-item]');
    closeSiblingItems(accordionItem);
    setAccordionItem(accordionItem, true);

    if (options.scroll !== false) {
      requestAnimationFrame(() => {
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    }

    if (options.focus) {
      const focusTarget = accordionItem?.querySelector('[data-k365-accordion-trigger]') || target;
      requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
    }

    return true;
  }

  function initializeRoot(root, options = {}) {
    if (!root) return;

    if (!initializedRoots.has(root)) {
      root.setAttribute('data-k365-enhanced', 'true');
      root.querySelectorAll('[data-k365-accordion-item]').forEach((item) => {
        const defaultOpen = Boolean(item.querySelector('[data-default-open]'));
        setAccordionItem(item, defaultOpen);
      });
      initializedRoots.add(root);
    }

    const anchor = options.anchor || '';
    if (anchor) {
      openAnchor(root, anchor, {
        scroll: options.scroll,
        focus: options.focus
      });
    }
  }

  function initializePageRoots() {
    const hashAnchor = getAnchorFromHash();
    document.querySelectorAll('[data-k365-size-guide]').forEach((root) => {
      if (root.closest('[data-k365-size-guide-dialog]')) return;
      initializeRoot(root, {
        anchor: hashAnchor,
        scroll: Boolean(hashAnchor),
        focus: false
      });
    });
  }

  function updateUnit(root, unit) {
    const metric = unit === 'metric';
    root.classList.toggle('is-metric', metric);
    root.querySelectorAll('[data-k365-unit]').forEach((button) => {
      const active = button.dataset.k365Unit === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function makeIdsUnique(root, prefix) {
    const idMap = new Map();

    root.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id;
      const newId = `${prefix}-${oldId}`;
      idMap.set(oldId, newId);
      element.id = newId;
    });

    ['aria-controls', 'aria-labelledby', 'aria-describedby', 'for'].forEach((attribute) => {
      root.querySelectorAll(`[${attribute}]`).forEach((element) => {
        const values = element.getAttribute(attribute).split(/\s+/);
        element.setAttribute(
          attribute,
          values.map((value) => idMap.get(value) || value).join(' ')
        );
      });
    });
  }

  async function loadDialog(dialog, anchor) {
    const content = dialog.querySelector('[data-k365-dialog-content]');
    const status = dialog.querySelector('[data-k365-dialog-status]');

    if (dialog.dataset.k365Loaded === 'true') {
      const root = content.querySelector('[data-k365-size-guide]');
      initializeRoot(root, { anchor, scroll: true, focus: true });
      return;
    }

    dialog.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Loading size guide…';

    const response = await fetch(dialog.dataset.k365Source, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error(`Size guide request failed with ${response.status}`);

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fetchedRoot = parsed.querySelector('[data-k365-size-guide]');
    if (!fetchedRoot) throw new Error('The size guide response did not contain the expected content.');

    const importedRoot = document.importNode(fetchedRoot, true);
    makeIdsUnique(importedRoot, dialog.id);
    content.replaceChildren(importedRoot);
    dialog.dataset.k365Loaded = 'true';
    dialog.removeAttribute('aria-busy');
    initializeRoot(importedRoot, { anchor, scroll: true, focus: true });
  }

  async function openDialog(trigger) {
    const dialogId = trigger.dataset.k365DialogId;
    const dialog = document.getElementById(dialogId);
    if (!dialog || typeof dialog.showModal !== 'function') return false;

    dialogOpeners.set(dialog, trigger);
    if (!dialog.open) dialog.showModal();

    try {
      await loadDialog(dialog, trigger.dataset.k365Target || '');
      return true;
    } catch (error) {
      console.error('[Kidz365 size guide]', error);
      if (dialog.open) dialog.close();
      window.location.assign(trigger.href);
      return false;
    }
  }

  document.addEventListener('click', (event) => {
    const unitButton = event.target.closest('[data-k365-unit]');
    if (unitButton) {
      const root = unitButton.closest('[data-k365-size-guide]');
      if (root) updateUnit(root, unitButton.dataset.k365Unit);
      return;
    }

    const accordionTrigger = event.target.closest('[data-k365-accordion-trigger]');
    if (accordionTrigger) {
      const item = accordionTrigger.closest('[data-k365-accordion-item]');
      const willOpen = accordionTrigger.getAttribute('aria-expanded') !== 'true';
      if (willOpen) closeSiblingItems(item);
      setAccordionItem(item, willOpen);
      return;
    }

    const jumpLink = event.target.closest('[data-k365-jump]');
    if (jumpLink) {
      const root = jumpLink.closest('[data-k365-size-guide]');
      const anchor = jumpLink.dataset.k365Jump;
      if (!root || !validAnchors.has(anchor)) return;

      event.preventDefault();
      const inDialog = Boolean(root.closest('[data-k365-size-guide-dialog]'));
      if (!inDialog) history.pushState(null, '', jumpLink.href);
      openAnchor(root, anchor, { scroll: true, focus: false });
      return;
    }

    const productLink = event.target.closest('[data-k365-size-guide-trigger]');
    if (productLink) {
      const modifiedClick = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
      if (modifiedClick) return;

      const dialog = document.getElementById(productLink.dataset.k365DialogId);
      if (!dialog || typeof dialog.showModal !== 'function') return;

      event.preventDefault();
      openDialog(productLink);
      return;
    }

    const closeButton = event.target.closest('[data-k365-dialog-close]');
    if (closeButton) {
      const dialog = closeButton.closest('[data-k365-size-guide-dialog]');
      if (dialog?.open) dialog.close();
    }
  });

  document.addEventListener('click', (event) => {
    const dialog = event.target.matches?.('[data-k365-size-guide-dialog]') ? event.target : null;
    if (dialog?.open) dialog.close();
  });

  document.addEventListener('close', (event) => {
    const dialog = event.target.matches?.('[data-k365-size-guide-dialog]') ? event.target : null;
    const opener = dialog ? dialogOpeners.get(dialog) : null;
    if (opener?.isConnected) opener.focus();
  }, true);

  window.addEventListener('hashchange', () => {
    const anchor = getAnchorFromHash();
    if (!anchor) return;

    document.querySelectorAll('[data-k365-size-guide]').forEach((root) => {
      if (!root.closest('[data-k365-size-guide-dialog]')) {
        initializeRoot(root, { anchor, scroll: true, focus: false });
      }
    });
  });

  window.K365SizeGuide = {
    initializeRoot,
    openAnchor
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePageRoots, { once: true });
  } else {
    initializePageRoots();
  }
})();
